import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { getSsoToken, getBeneficiaryDetails, WalletAccount } from "../../api";
import { useAuth } from "../../Context/Auth/index";
import { useOverview } from "../../Context/AccountOverview/index";

const SsoLogin = () => {
  const { token } = useParams();

  const [searchParams] = useSearchParams();
  const action = searchParams.get("action");
  const src = searchParams.get("source");

  const navigate = useNavigate();
  const { setStateVariable } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const {
    setIsOpen,
    setCurrentFlow,
    useGlobalAccount,
    setStateVariable: investingStateVariable,
    setInvestmentAccount,
    setBeneficiarySelected,
  } = useOverview();

  const { data: globalData } = useGlobalAccount();

  useEffect(() => {
    const doSsoLogin = async () => {
      try {
        if (!token) {
          setError("Invalid SSO link");
          setLoading(false);
          return;
        }

        sessionStorage.removeItem("accessToken");
        sessionStorage.removeItem("refreshToken");
        sessionStorage.removeItem("userId");

        const response = await getSsoToken(token);

        if (response?.status === 200) {
          // const userDetails = await getUserDetails(
          //   response.data?.data?.data?.mcb_userId
          // );
          if (src) {
            sessionStorage.setItem("ssoUrlSource", true);
          }

          const { data } = response;
          const user = data?.data?.user;

          if (user) {
            // if (globalData?.data?.status !== "APPROVED") {
            //   navigate("/homeVerification", { replace: true });
            //   setLoading(false);
            //   return;
            // }

            if (
              user?.status === "PENDING" &&
              (user?.currentStep === "ACTIVE" ||
                user?.currentStep === "SUBMITTED" ||
                user?.currentStep === "SELFIEE_UPLOAD" ||
                user?.currentStep === "IN_PROGRESS")
            ) {
              if (action === "homeVerification") {
                navigate("/homeVerification", {
                  replace: true,
                });
                setLoading(false);
                return;
              }
            }

            if (
              user?.status === "APPROVED" &&
              user?.currentStep === "ACTIVE" &&
              globalData?.data?.status === "APPROVED"
            ) {
              if (action === "openModal") {
                setIsOpen(true);
                setCurrentFlow("AddFunds");
              }
              // 'Send Money TO Investing '
              if (action === "AddFunds") {
                try {
                  // Check wallet API for wallet amount
                  const walletResponse = await WalletAccount();
                  const accounts = walletResponse?.data?.accounts || [];

                  // Check if any wallet account has balance > 0
                  const hasWalletBalance = accounts.some(
                    (account) => account?.balance > 0,
                  );

                  // If wallet amount is not available, show AddFunds flow
                  if (!hasWalletBalance) {
                    setIsOpen(true);
                    setCurrentFlow("AddFunds");
                  } else {
                    // If wallet amount is present, fetch beneficiary details
                    const beneficiaryResponse = await getBeneficiaryDetails([
                      "beneficiaryDetails",
                      { status: "APPROVED" },
                    ]);

                    // Get all beneficiaries
                    const beneficiaries =
                      beneficiaryResponse?.data?.beneficiaries || [];

                    // Find the investment account beneficiary
                    const investmentAccount = beneficiaries?.find(
                      (bene) => bene?.beneType === "INVESTING",
                    );

                    if (investmentAccount) {
                      // Set the flow state
                      setIsOpen(true);
                      setCurrentFlow("SendMoney");
                      investingStateVariable(2);
                      setInvestmentAccount(true);

                      // Pass the investment account data
                      setBeneficiarySelected(investmentAccount);
                    }
                  }
                } catch (err) {
                  console.error(
                    "Failed to fetch wallet or beneficiary details:",
                    err,
                  );
                }
              }
              navigate("/home", {
                replace: true,
              });
              setLoading(false);
              return;
            }

            if (user?.currentStep === "REGISTER") {
              setStateVariable(1);
              navigate("/details", {
                replace: true,
              });
              setLoading(false);
              return;
            }
            if (user?.currentStep === "PERSONAL_INFO") {
              setStateVariable(2);
              navigate("/details", {
                replace: true,
              });
              setLoading(false);
              return;
            }

            if (user?.currentStep === "SIGNUP") {
              setStateVariable(3);
              navigate("/details", {
                replace: true,
              });
              setLoading(false);
              return;
            }
            if (user?.currentStep === "DOC_UPLOAD") {
              setStateVariable(5);
              navigate("/details", {
                replace: true,
              });
              setLoading(false);
              return;
            }

            if (user?.currentStep === "SELFIEE_UPLOAD") {
              navigate("/redirect?status=success", {
                replace: true,
              });
              setLoading(false);
              return;
            }

            navigate("/homeVerification", {
              replace: true,
            });
            setLoading(false);
          }
        }
      } catch (err) {
        console.error(err);
        setError("Something went wrong during SSO login");
      } finally {
        setLoading(false);
      }
    };

    doSsoLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading)
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-[1.2rem] text-gray-800">
        <div className="w-10 h-10 border-4 border-t-4 border-gray-200 rounded-full loader border-t-blue-500 animate-spin" />
      </div>
    );

  if (error)
    return (
      <div className="flex items-center justify-center min-h-[60vh] bg-gray-50">
        <div className="flex flex-col items-center w-full max-w-sm p-8 bg-white border border-gray-200 shadow-lg rounded-xl">
          <div className="flex items-center justify-center w-12 h-12 mb-4 bg-red-100 rounded-full">
            <svg
              className="w-6 h-6 text-red-500"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-xl font-semibold text-red-600">
            SSO Login Failed
          </h3>
          <p className="mb-6 text-center text-gray-700">{error}</p>
          <a
            href="/login"
            className="inline-block px-5 py-2 font-medium text-white transition bg-blue-500 rounded-lg shadow hover:bg-blue-600"
          >
            Go back to login
          </a>
        </div>
      </div>
    );
};

export default SsoLogin;
